import React, { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { format } from "date-fns";
import type { Locale } from "date-fns";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Spinner, Text } from "@shopify/polaris";
import {
  setModalxComponent,
  setModalxComponentProps,
  setModalxOptions,
  setModalxStyle,
} from "components/modalx/modalx.slice";
import { fetchEmployeeLeaveRequests } from "services/leave-requests.service";
import { serializedLeaveToRecord } from "features/leave-calendar/leaveRecordMappers";
import {
  dayKey,
  isToday,
  leaveCalendarDateLocale,
} from "features/leave-calendar/leaveCalendarDates";
import { leaveCalendarLeaveTypeI18nKey } from "features/leave-calendar/leaveCalendar.constants";
import {
  LEAVE_PENDING_BG,
  LEAVE_TYPE_BG,
  LEAVE_TYPE_BORDER,
} from "features/leave-calendar/leaveCalendarColors";
import { LeaveCalendarLegend } from "features/leave-calendar/LeaveCalendarLegend";
import type { LeaveRecord } from "features/leave-calendar/leaveCalendar.types";
import { API_URL } from "constants.js";
import { canOpenLeavePdf } from "./leave-row-model";
import {
  buildYearLeaveDayMap,
  getMonthGridWeeks,
  weekdayInitials,
} from "./leaveWallCalendar.helpers";

// A phone gets bigger targets and text than a desktop: the arrow is 44px, a day 36px high.
const YEAR_ARROW =
  "inline-flex items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition-colors hover:bg-slate-50 max-sm:size-11 sm:p-1.5";

export interface UserProfileLeavesCalendarProps {
  companyId: string;
  userId: string;
  /** Shown as the leave's owner if the view modal (no-document fallback) is opened. */
  employeeName?: string;
  employeeEmail?: string;
  /** Changes when the leaves changed elsewhere on the page, so the year is fetched again. */
  refreshKey?: number;
}

/**
 * Employee card → Atostogos tab: a 12-month wall calendar for one year of the
 * person's leaves, coloured exactly like the company leave calendar
 * (LeaveCalendarUserRow / LeaveCalendarLegend).
 */
export function UserProfileLeavesCalendar({
  companyId,
  userId,
  employeeName,
  employeeEmail,
  refreshKey = 0,
}: UserProfileLeavesCalendarProps) {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [leaves, setLeaves] = useState<LeaveRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!companyId || !userId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        // No page/pageSize: same convention the company calendar uses to fetch a whole range in one call.
        const { leaveRequests } = await fetchEmployeeLeaveRequests(
          companyId,
          userId,
          {
            from: `${year}-01-01`,
            to: `${year}-12-31`,
          }
        );
        if (cancelled) return;
        setLeaves(leaveRequests.map(serializedLeaveToRecord));
      } catch (e: unknown) {
        if (!cancelled) {
          setError(
            e instanceof Error ? e.message : t("profile_leaves_load_error")
          );
          setLeaves([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, userId, year, refreshKey, t]);

  const dayMap = useMemo(
    () => buildYearLeaveDayMap(leaves, year),
    [leaves, year]
  );

  const lang = (i18n as any).language as string | undefined;
  const locale = useMemo(() => leaveCalendarDateLocale(lang), [lang]);
  const weekdays = useMemo(() => weekdayInitials(locale), [locale]);

  const leaveTitle = (leaveItem: LeaveRecord) => {
    const typeLabel = t(leaveCalendarLeaveTypeI18nKey(leaveItem.leaveType), {
      defaultValue: leaveItem.leaveType,
    });
    const statusLabel = t(
      `leave_calendar_status_${leaveItem.status || "pending_approval"}`
    );
    const range =
      leaveItem.startDate === leaveItem.endDate
        ? leaveItem.startDate
        : `${leaveItem.startDate} → ${leaveItem.endDate}`;
    return [typeLabel, range, statusLabel].filter(Boolean).join(" · ");
  };

  const openLeaveDocument = (leaveItem: LeaveRecord) => {
    if (leaveItem.fileId && canOpenLeavePdf(leaveItem)) {
      dispatch(setModalxComponent("PdfView"));
      dispatch(setModalxStyle("large"));
      dispatch(setModalxComponentProps({ id: leaveItem.fileId, companyId }));
      return;
    }
    if (leaveItem.fileId) {
      window.open(
        `${API_URL}/${companyId}/files/download/${leaveItem.fileId}`,
        "_blank",
        "noopener,noreferrer"
      );
      return;
    }
    // No document (e.g. isRedacted, or none generated yet): fall back to the leave detail modal.
    dispatch(setModalxComponent("LeaveCalendarLeaveModal"));
    dispatch(setModalxOptions({ title: t("leave_calendar_modal_view_title") }));
    dispatch(
      setModalxComponentProps({
        mode: "view",
        leave: leaveItem,
        companyId,
        users: employeeName
          ? [{ id: userId, name: employeeName, email: employeeEmail || "" }]
          : [],
        readOnly: true,
        allLeaves: leaves,
      })
    );
  };

  return (
    <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <Text as="h2" variant="headingMd">
          {t("leave_calendar")}
        </Text>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className={YEAR_ARROW}
            onClick={() => setYear((y) => y - 1)}
            aria-label={t("previous_year")}
          >
            <ChevronLeft size={16} />
          </button>
          <span className="min-w-[3.5rem] text-center text-sm font-extrabold text-slate-700">
            {year}
          </span>
          <button
            type="button"
            className={YEAR_ARROW}
            onClick={() => setYear((y) => y + 1)}
            aria-label={t("next_year")}
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {error && <p className="mb-3 text-sm text-rose-600">{error}</p>}

      {loading ? (
        <div className="flex justify-center py-10">
          <Spinner size="small" accessibilityLabel={t("loading")} />
        </div>
      ) : (
        <div className="flex flex-col">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {Array.from({ length: 12 }, (_, monthIndex) => (
              <MonthGrid
                key={monthIndex}
                year={year}
                monthIndex={monthIndex}
                weekdays={weekdays}
                locale={locale}
                dayMap={dayMap}
                onLeaveClick={openLeaveDocument}
                leaveTitle={leaveTitle}
              />
            ))}
          </div>
          {/* On a phone the legend leads: the colours are read before the 12 months. */}
          <div className="mb-4 max-sm:order-first sm:order-none sm:mb-0 sm:mt-4">
            <LeaveCalendarLegend compact />
          </div>
        </div>
      )}
    </div>
  );
}

// On a phone a pending day is told from an approved one by its dashed outline, not by the colour alone.
const PENDING_DAY = `${LEAVE_PENDING_BG} max-sm:border-2 max-sm:border-dashed max-sm:!border-amber-600`;

interface MonthGridProps {
  year: number;
  monthIndex: number;
  weekdays: string[];
  locale: Locale;
  dayMap: Map<string, LeaveRecord>;
  onLeaveClick: (leave: LeaveRecord) => void;
  leaveTitle: (leave: LeaveRecord) => string;
}

function MonthGrid({
  year,
  monthIndex,
  weekdays,
  locale,
  dayMap,
  onLeaveClick,
  leaveTitle,
}: MonthGridProps) {
  const weeks = useMemo(
    () => getMonthGridWeeks(year, monthIndex),
    [year, monthIndex]
  );
  const monthLabel = format(new Date(year, monthIndex, 1), "LLLL", { locale });

  return (
    <div
      className="rounded-xl border border-slate-100 bg-slate-50/30 p-2"
      data-testid="wall-calendar-month"
      data-month={monthIndex}
    >
      <div className="mb-1.5 text-center text-sm font-extrabold uppercase tracking-wide text-slate-600 sm:text-[11px]">
        {monthLabel}
      </div>
      <div className="grid grid-cols-7 gap-[2px]">
        {weekdays.map((w, i) => (
          <div
            key={i}
            className="text-center text-xs font-bold uppercase text-slate-400 sm:text-[9px]"
          >
            {w}
          </div>
        ))}
        {weeks.flat().map((d) => {
          if (d.getMonth() !== monthIndex) {
            return (
              <div key={d.toISOString()} className="h-9 sm:h-6" aria-hidden="true" />
            );
          }
          const key = dayKey(d);
          const leaveItem = dayMap.get(key);
          const today = isToday(d);
          const dayNumber = format(d, "d");

          if (!leaveItem) {
            return (
              <div key={key} className="flex h-9 items-center justify-center sm:h-6">
                <span
                  className={clsx(
                    "flex items-center justify-center text-xs sm:text-[10px]",
                    today
                      ? "h-6 w-6 rounded-full sm:h-5 sm:w-5 bg-teal-600 font-extrabold text-white shadow-sm shadow-teal-600/40"
                      : "text-slate-600"
                  )}
                >
                  {dayNumber}
                </span>
              </div>
            );
          }

          const title = leaveTitle(leaveItem);
          return (
            <button
              key={key}
              type="button"
              data-testid="wall-calendar-leave-day"
              data-date={key}
              className={clsx(
                "flex h-9 w-full cursor-pointer flex-col overflow-hidden rounded sm:h-6",
                today && "ring-2 ring-teal-500"
              )}
              title={title}
              aria-label={title}
              onClick={() => onLeaveClick(leaveItem)}
            >
              {leaveItem.duration === "half_day" && (
                <span className="min-h-[50%] shrink-0 bg-transparent" />
              )}
              {leaveItem.duration === "hourly" && (
                <span className="min-h-[75%] shrink-0 bg-transparent" />
              )}
              <span
                className={clsx(
                  "flex flex-1 items-center justify-center border text-xs font-bold sm:text-[10px]",
                  leaveItem.duration === "half_day"
                    ? "min-h-[50%]"
                    : leaveItem.duration === "hourly"
                    ? "min-h-[25%]"
                    : "h-full",
                  leaveItem.status === "pending_approval"
                    ? PENDING_DAY
                    : clsx(
                        LEAVE_TYPE_BG[leaveItem.type],
                        LEAVE_TYPE_BORDER[leaveItem.type],
                        "text-white"
                      )
                )}
              >
                {dayNumber}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
